import type { FC } from "react";

import { cn } from "@/shared/utils/core";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { ACCOUNT_TYPES } from "@/shared/constants/estateCategories";
import { OWNERSHIP_TYPES } from "@/shared/constants/ownershipTypes";
import { currencyInput } from "@/shared/utils/currencyInput";
import { buildAddress, parseAddress, toStateAbbreviation } from "@/shared/utils/addressUtils";
import { isValidZipCode } from "@/shared/utils/validators";
import type { AddressParts } from "@/shared/utils/addressUtils";

const PROPERTY_TYPES = ACCOUNT_TYPES["Properties"];

export interface PropertyFormValues {
  streetAddress: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  ownershipType: string;
  coOwnerNames: string;
  value: string;
  deedFile: File | null;
}

export const EMPTY_PROPERTY_FORM: PropertyFormValues = {
  streetAddress: "",
  apartment: "",
  city: "",
  state: "",
  zipCode: "",
  propertyType: "",
  ownershipType: "",
  coOwnerNames: "",
  value: "",
  deedFile: null,
};

const SHARED_OWNERSHIP_SLUGS = new Set([
  "jtwros",
  "tbe",
  "tic",
  "community_property",
  "community_property_survivorship",
]);

export interface PropertyFieldErrors {
  streetAddress?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  propertyType?: string;
}

export function validatePropertyFields(form: PropertyFormValues): PropertyFieldErrors {
  const errors: PropertyFieldErrors = {};
  if (!form.streetAddress.trim()) errors.streetAddress = "Please enter a street address";
  if (!form.city.trim()) errors.city = "Please enter a city";
  if (!form.state) errors.state = "Please select a state";
  if (!form.zipCode.trim()) errors.zipCode = "Please enter a zip code";
  else if (!isValidZipCode(form.zipCode)) errors.zipCode = "Please enter a valid zip code";
  if (!form.propertyType) errors.propertyType = "Please select a property type";
  return errors;
}

export function propertyToItemFields(form: PropertyFormValues) {
  return {
    item_type: form.propertyType || undefined,
    balance: parseFloat(form.value) || 0,
    ownership: form.ownershipType || null,
    metadata: {
      street_address: form.streetAddress || undefined,
      apartment: form.apartment || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip_code: form.zipCode || undefined,
      co_owner_names: form.coOwnerNames.trim() || undefined,
    },
  };
}

export function canSubmitProperty(form: PropertyFormValues): boolean {
  return !!(form.streetAddress.trim() && form.city.trim() && form.state && form.propertyType);
}

export function buildItemName(form: PropertyFormValues): string {
  return buildAddress(form);
}

export function parseItemNameToForm(
  itemName: string,
  itemType: string,
  balance: number,
  metadata?: Record<string, unknown>,
  ownership?: string | null,
): PropertyFormValues {
  const parsedAddress = parseAddress(itemName);
  const ownershipType = ownership || (metadata?.ownership_type as string) || "";
  return {
    streetAddress: (metadata?.street_address as string) || parsedAddress.streetAddress,
    apartment: (metadata?.apartment as string) || parsedAddress.apartment,
    city: (metadata?.city as string) || parsedAddress.city,
    state: toStateAbbreviation((metadata?.state as string) || parsedAddress.state),
    zipCode: (metadata?.zip_code as string) || parsedAddress.zipCode,
    propertyType: itemType,
    ownershipType,
    coOwnerNames: (metadata?.co_owner_names as string) || "",
    value: balance ? String(balance) : "",
    deedFile: null,
  };
}

interface PropertyFormProps {
  form: PropertyFormValues;
  onChange: (form: PropertyFormValues) => void;
  fieldErrors?: PropertyFieldErrors;
  showValue?: boolean;
  showDeedUpload?: boolean;
  existingDeedFileName?: string | null;
  onDeedRemoved?: () => void;
  portalContainer?: HTMLElement | null;
}

export const PropertyForm: FC<PropertyFormProps> = ({
  form,
  onChange,
  fieldErrors,
  showValue = true,
  showDeedUpload = true,
  existingDeedFileName,
  onDeedRemoved,
  portalContainer,
}) => {
  const handleAddressChange = (updated: AddressParts) => {
    onChange({ ...form, ...updated });
  };

  const valueInput = currencyInput(form.value, (val) => onChange({ ...form, value: val }));

  return (
    <div className="flex flex-col gap-4">
      <AddressPicker
        value={form}
        onChange={handleAddressChange}
        stateFormat="abbreviation"
        showRequired
        portalContainer={portalContainer}
        errors={fieldErrors}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label className="text-sm font-medium" required>
            Property type
          </Label>
          <Select
            value={form.propertyType}
            onValueChange={(val) => onChange({ ...form, propertyType: val })}
          >
            <SelectTrigger className={cn("text-sm", fieldErrors?.propertyType && "border-error")}>
              <SelectValue placeholder="Select property category" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((type) => (
                <SelectItem key={type} value={type} className="text-sm">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={fieldErrors?.propertyType} />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Ownership type <span className="text-tertiary">(optional)</span>
          </Label>
          <Select
            value={form.ownershipType}
            onValueChange={(val) => onChange({ ...form, ownershipType: val })}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select ownership type" />
            </SelectTrigger>
            <SelectContent>
              {OWNERSHIP_TYPES.map((t) => (
                <SelectItem key={t.slug} value={t.slug} className="text-sm">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {SHARED_OWNERSHIP_SLUGS.has(form.ownershipType) && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Co-owner's name(s) <span className="text-tertiary">(optional)</span>
          </Label>
          <Input
            value={form.coOwnerNames}
            onChange={(e) => onChange({ ...form, coOwnerNames: e.target.value })}
            placeholder="e.g., Jane Smith, Robert Johnson"
          />
        </div>
      )}

      {showValue && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Approximate value <span className="text-tertiary">(optional)</span>
          </Label>
          <CurrencyInput
            value={valueInput.displayValue}
            onChange={valueInput.handleChange}
            placeholder="0.00"
          />
        </div>
      )}

      {showDeedUpload && (
        <UploadZone
          label="Property deed"
          labelSuffix="(optional)"
          file={form.deedFile}
          onFileSelect={(file) => onChange({ ...form, deedFile: file })}
          onFileRemove={() => {
            onChange({ ...form, deedFile: null });
            onDeedRemoved?.();
          }}
          existingFileName={existingDeedFileName}
        />
      )}
    </div>
  );
};
